import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Send, EyeOff, Unlink, QrCode, Link2, Power, PowerOff, Code2, Copy, Check } from 'lucide-react';
import { AutoPreview } from './TemplateRenderer';
import ConfirmDialog from './ConfirmDialog';
import { api, apiUrl } from '../api';

// Copy-ready curl for one screen. The key is never rendered here — the user
// pastes their own from Settings, or exports it as $PUTTV_API_KEY — so a
// screenshot of this page can't leak credentials.
function ApiExample({ device, templateId }) {
  const [copied, setCopied] = useState(false);
  const base = apiUrl('') || window.location.origin;
  const tpl = templateId || '<template-id>';
  const snippet =
    `curl -X POST ${base}/api/v1/devices/${device.deviceUUID}/push \\\n` +
    `  -H "X-API-Key: $PUTTV_API_KEY" \\\n` +
    `  -H 'Content-Type: application/json' \\\n` +
    `  -d '{"template_id": "${tpl}"}'`;

  const copy = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <div className="api-example">
      <div className="api-example-head">
        <span>Push to this screen from your own system</span>
        <button className="btn btn-ghost btn-sm" onClick={copy} type="button">
          {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
        </button>
      </div>
      <pre className="mono api-example-code">{snippet}</pre>
      <div className="api-example-foot">
        Get a key in <strong>Settings</strong>. Also available:{' '}
        <code className="mono">/clear</code>, <code className="mono">/power</code>,{' '}
        <code className="mono">/variables</code>
        {device.group && <> · this screen is in group <code className="mono">{device.group}</code>, so{' '}
          <code className="mono">/api/v1/groups/{device.group}/push</code> updates it together with the rest</>}
        . <a href={apiUrl('/api/docs')} target="_blank" rel="noreferrer">Full API reference</a>
      </div>
    </div>
  );
}

// Flat SMPTE-style test card: what a screen shows in the UI when it has no
// content assigned. On-brand for TV, and instantly readable as "no signal".
function TestCard({ label = 'NO SIGNAL' }) {
  const bars = ['#c8c8c2', '#b3b7ad', '#f0a11c', '#9ba1a8', '#8b8f98', '#767b85', '#5d6169'];
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex' }} aria-hidden="true">
      {bars.map((c, i) => <div key={i} style={{ flex: 1, background: c }} />)}
      <span style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.14em', fontWeight: 700,
        color: '#16181d', background: 'rgba(244,244,241,0.55)',
      }}>{label}</span>
    </div>
  );
}

// "3m ago" / "2h ago" for last_seen; null-safe.
function timeAgo(iso) {
  if (!iso) return null;
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// "Pair a new TV" helper: the TV shows a QR code (a link to /pair?token=…).
// Scanning it on a phone is the main flow; this panel also accepts the code
// pasted/typed manually for desktop users who can't scan.
function PairPanel({ userId, userEmail, onPaired }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok, message }

  const extractToken = (raw) => {
    const m = String(raw).match(/[?&]token=([^&\s]+)/);
    return (m ? m[1] : String(raw).trim()).trim();
  };

  const pair = async (e) => {
    e.preventDefault();
    const token = extractToken(code);
    if (!token) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await api.claimPairing(token, userId, userEmail);
      setResult({ ok: true, message: `TV paired (${(res.deviceUUID || '').slice(0, 8)}…). It will show your content shortly.` });
      setCode('');
      onPaired();
    } catch (err) {
      setResult({ ok: false, message: err.message || 'Pairing failed.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pair-card">
      <div className="pair-head"><QrCode size={16} /> Pair a new TV</div>
      <p>
        Plug the box into your TV — it boots showing a QR code. Scan it with your phone
        while signed in, or paste the pairing link / code shown under the QR here.
      </p>
      <form onSubmit={pair} className="pair-form">
        <input className="input" value={code} onChange={(e) => setCode(e.target.value)}
          placeholder="Pairing link or code from the TV screen" />
        <button className="btn btn-primary" type="submit" disabled={busy || !code.trim()}>
          <Link2 size={16} /> {busy ? 'Pairing…' : 'Pair'}
        </button>
      </form>
      {result && (
        <p style={{ marginTop: 10, marginBottom: 0, fontSize: 13, color: result.ok ? 'var(--ok)' : 'var(--danger)' }}>
          {result.message}
        </p>
      )}
    </div>
  );
}

export default function DeviceManager({ socket, userId, userEmail, onPowerAll }) {
  const [devices, setDevices] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState({}); // deviceUUID -> templateId
  const [error, setError] = useState('');
  const [unclaiming, setUnclaiming] = useState(null); // device pending unpair confirmation
  const [expanded, setExpanded] = useState(null);     // deviceUUID whose details panel is open
  const [draft, setDraft] = useState({});             // deviceUUID -> { name, group } being edited

  const load = useCallback(async () => {
    try {
      const [d, t] = [await api.listDevices(userId), await api.listTemplates(userId)];
      setDevices(d.devices);
      setTemplates(t.templates);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [userId]);

  useEffect(() => {
    load();
    const i = setInterval(load, 8000); // refresh online status periodically
    return () => clearInterval(i);
  }, [load]);

  // Push/clear go through the REST API (Bearer-token auth, the same path the
  // rest of the dashboard uses) rather than the Socket.IO relay, whose token
  // identify was unreliable in the browser and left commands unauthorized.
  const push = async (deviceUUID) => {
    const templateId = selected[deviceUUID] || (templates[0] && templates[0].id);
    if (!templateId) return;
    try {
      await api.pushToDevice(deviceUUID, templateId);
    } catch (e) {
      setError(e.message);
    }
    setTimeout(load, 1000);
  };

  const clear = async (deviceUUID) => {
    try {
      await api.clearDevice(deviceUUID);
    } catch (e) {
      setError(e.message);
    }
    setTimeout(load, 1000);
  };

  const unclaim = async (deviceUUID) => {
    await api.unclaimDevice(deviceUUID, userId);
    setUnclaiming(null);
    load();
  };

  const openDetails = (d) => {
    const open = expanded === d.deviceUUID;
    setExpanded(open ? null : d.deviceUUID);
    if (!open) setDraft((s) => ({ ...s, [d.deviceUUID]: { name: d.name || '', group: d.group || '' } }));
  };

  // Empty group clears membership; the server takes null for that.
  const saveDetails = async (deviceUUID) => {
    const d = draft[deviceUUID] || {};
    try {
      await api.renameDevice(deviceUUID, {
        ...(d.name.trim() ? { name: d.name.trim() } : {}),
        group: d.group.trim() || null,
      });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const templateById = (id) => templates.find((t) => t.id === id);

  return (
    <div>
      <div className="page-head">
        <div className="page-title">
          <h2>Screens</h2>
          <p>{devices.length} paired screen{devices.length === 1 ? '' : 's'}</p>
        </div>
        <div className="page-actions">
          {onPowerAll && (
            <>
              <button className="btn btn-ghost" title="Power on all TVs (HDMI-CEC)"
                onClick={() => onPowerAll('power_on')}>
                <Power size={16} /> All on
              </button>
              <button className="btn btn-ghost" title="Power off all TVs (HDMI-CEC)"
                onClick={() => onPowerAll('power_off')}>
                <PowerOff size={16} /> All off
              </button>
            </>
          )}
          <button className="btn btn-ghost btn-icon" title="Refresh" onClick={load}>
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {error && <p className="error-text">{error}</p>}

      <PairPanel userId={userId} userEmail={userEmail} onPaired={load} />

      {devices.length === 0 && (
        <div className="empty">No paired TVs yet. Pair one by scanning the QR code shown on the screen.</div>
      )}

      {devices.length > 0 && (
        <div className="fleet">
          {devices.map((d) => {
            const assigned = templateById(d.assignedTemplateId);
            const seen = timeAgo(d.lastSeen);
            const open = expanded === d.deviceUUID;
            const edit = draft[d.deviceUUID] || { name: '', group: '' };
            return (
              <div key={d.deviceUUID} className={`fleet-row${open ? ' expanded' : ''}`}>
                <div className="fleet-thumb">
                  {assigned ? <AutoPreview template={assigned.json} /> : <TestCard />}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="fleet-name">
                    {d.name || d.deviceUUID.slice(0, 8)}
                    {d.group && <span className="group-tag">{d.group}</span>}
                  </div>
                  <div className="mono fleet-id">{d.deviceUUID.slice(0, 18)}…</div>
                  <div className="fleet-status">
                    <span className={`status-dot ${d.connected ? 'on' : 'off'}`} />
                    {d.connected ? 'Online' : `Offline${seen ? ` · seen ${seen}` : ''}`}
                    <span className="sep">·</span>
                    Showing: {assigned?.name || '—'}
                  </div>
                </div>
                <div className="fleet-actions">
                  <select className="select" value={selected[d.deviceUUID] || ''}
                    onChange={(e) => setSelected((s) => ({ ...s, [d.deviceUUID]: e.target.value }))}>
                    <option value="">Select template…</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <button className="btn btn-primary" onClick={() => push(d.deviceUUID)} title="Push to TV">
                    <Send size={16} /> Push
                  </button>
                  <button className="btn btn-ghost btn-icon" onClick={() => clear(d.deviceUUID)} title="Clear screen">
                    <EyeOff size={16} />
                  </button>
                  <button className={`btn btn-icon ${open ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => openDetails(d)} title="Name, group & API">
                    <Code2 size={16} />
                  </button>
                  <button className="btn btn-danger btn-icon" onClick={() => setUnclaiming(d)} title="Unpair from your account">
                    <Unlink size={16} />
                  </button>
                </div>

                {open && (
                  <div className="fleet-details">
                    <div className="detail-fields">
                      <label>
                        <span>Name</span>
                        <input className="input" value={edit.name} maxLength={100}
                          placeholder={d.deviceUUID.slice(0, 8)}
                          onChange={(e) => setDraft((s) => ({ ...s, [d.deviceUUID]: { ...edit, name: e.target.value } }))} />
                      </label>
                      <label>
                        <span>Group</span>
                        <input className="input" value={edit.group} maxLength={100}
                          placeholder="e.g. lobby — leave blank for none"
                          onChange={(e) => setDraft((s) => ({ ...s, [d.deviceUUID]: { ...edit, group: e.target.value } }))} />
                      </label>
                      <button className="btn btn-primary" onClick={() => saveDetails(d.deviceUUID)}>Save</button>
                    </div>
                    <ApiExample device={d} templateId={selected[d.deviceUUID] || d.assignedTemplateId} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!unclaiming}
        danger
        title={`Unpair “${unclaiming?.name || (unclaiming?.deviceUUID || '').slice(0, 8)}”?`}
        body="The TV goes back to its pairing screen and stops showing your content. You can re-pair it any time by scanning its QR code."
        confirmLabel="Unpair"
        onConfirm={() => unclaim(unclaiming.deviceUUID)}
        onCancel={() => setUnclaiming(null)}
      />
    </div>
  );
}
